import {
  IAudit,
  IAuditNode,
  IPatient,
  IPrescriptionRequest,
  IReport,
  ISingleOrder,
  ITemplate,
} from '@alberta/konexi-shared';
import { DocumentGenerator, ReportGeneratable } from '@alberta/swodoc-document-generator';
import { AuditData } from '@alberta/swodoc-document-generator/dist/domain/Audit/Audit';
import { Inject, Injectable } from '@angular/core';
import { LoadingController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { assignIn, cloneDeep } from 'lodash';
import { AttachmentComponentModel } from 'src/app/business/attachment/attachment-component-model';
import { PrescriptionChain } from 'src/app/business/prescription/prescription-chain/prescription-chain';
// tslint:disable-next-line:max-line-length
import { PrescriptionRequestComponentModel } from 'src/app/business/prescription/prescription-request-component-model';
import { PrescriptionRequestViewModel } from 'src/app/business/prescription/view-models/prescription-request-view-model';
import { SingleOrderComponentModel } from 'src/app/business/single-order/single-order-component-model';
import { SingleOrderViewModel } from 'src/app/business/single-order/single-order-view-model';
import { map } from 'src/app/common/mapper/mapper';
import { PrescriptionRequestDB, ReportDB, SingleOrderDB } from 'src/app/common/repository/databases';
import { observe } from 'src/app/common/tracking';
import { TreeArray } from 'src/app/common/tree-array/tree-array';
// tslint:disable-next-line:max-line-length
import { SingleOrderPrescriptionStatusComponent } from 'src/app/patient/components/patient-detail/sections/integratedcare/single-order-detail/single-order-prescription-status/single-order-prescription-status.component';
import { SwodocCreateService } from 'src/app/swodoc/services/swodoc-create.service';
// Stimulsoft needs to importet from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer'
// import { Stimulsoft } from 'stimulsoft-reports-js' breaks Alberta
import { Stimulsoft } from 'stimulsoft-reports-js/Scripts/stimulsoft.viewer';

import { SQLiteQueryBuilder } from '@common/storage/sqlite-query-builder';
import { AttachmentServiceToken, IAttachmentService } from '@services/attachment/attachment-service.interface';
import { environment } from 'src/environments/environment';
import { DocumentCreateComponent } from '../../components/document/document-create/document-create.component';
import { ReportModal } from '../../components/report-modal/report-modal';
import { PrescriptionNodeValue } from '../../models/prescription/prescription-node-value.model';
import { PrescriptionRequestDto } from '../../models/prescription/prescription-request-dto.model';
import { SingleOrderDto } from '../../models/single-order/single-order-dto.model';
import { AuditAssetLoader } from '../audit/audit-asset-loader';
import { AuditReportGenerator } from '../audit/audit-report-generator';
import { QueryService } from '../query/query.service';

@Injectable({ providedIn: 'root' })
export class PrescriptionService {
  constructor(
    private _modalController: ModalController,
    private _swodocCreateService: SwodocCreateService,
    private _attachmentModel: AttachmentComponentModel,
    @Inject(AttachmentServiceToken) private _attachmentService: IAttachmentService,
    private _prescriptionChain: PrescriptionChain,
    private _queryService: QueryService,
    private _prescriptionRequestComponentModel: PrescriptionRequestComponentModel,
    private _loadingController: LoadingController,
    private _translateService: TranslateService
  ) {}

  public async selectTemplate(patient: IPatient): Promise<ITemplate> {
    const prescriptionModal = await this._modalController.create({
      component: DocumentCreateComponent,
      backdropDismiss: true,
      componentProps: {
        patient: cloneDeep(patient),
        entityType: 'prescription',
      },
      id: 'documentCreateModal',
      cssClass: 'fullPageModal',
    });
    await prescriptionModal.present();
    const documentResult = await prescriptionModal.onDidDismiss();
    if (documentResult?.data) {
      return documentResult.data as ITemplate;
    }
  }

  public async addPrescriptionRequest(
    patient: IPatient,
    selectedTemplate: ITemplate,
    selectedSingleOrders: ISingleOrder[],
    previousPrescriptionRequest?: IPrescriptionRequest
  ): Promise<IAudit | undefined> {
    return this._swodocCreateService.preparePrescriptionAudit(
      selectedTemplate,
      {
        patient: patient,
        selectedSingleOrders,
      },
      previousPrescriptionRequest
    );
  }

  public async getPreviousPrescriptionRequestWithinSameIntegratedCareAndTemplate(
    templateId: string,
    patientId: string,
    integratedCareIds: string[]
  ): Promise<IPrescriptionRequest | undefined> {
    // Step 1: Fetch prescription requests for the same patient and template
    const previousPrescriptionRequestsForSamePatient = await this.fetchPrescriptionRequestsByPatientAndTemplate(
      patientId,
      templateId
    );

    // Step 2: Filter requests based on integrated care IDs
    const filteredRequests = await this.filterRequestsByIntegratedCareIds(
      previousPrescriptionRequestsForSamePatient,
      integratedCareIds
    );

    // Step 3: Sort and get the most recent request
    return this.getMostRecentRequest(filteredRequests);
  }

  /**
   * Fetches prescription requests for a specific patient and template
   */
  private async fetchPrescriptionRequestsByPatientAndTemplate(
    patientId: string,
    templateId: string
  ): Promise<IPrescriptionRequest[]> {
    return await this._queryService.raw<IPrescriptionRequest>(
      {
        sql: new SQLiteQueryBuilder()
          .select('value')
          .fromFts(PrescriptionRequestDB)
          .innerJoin(PrescriptionRequestDB, 'id', '=', 'key')
          .where('patientId', '=', patientId)
          .and('templateId', '=', templateId)
          .asString(),
        aggregation: [
          {
            $match: {
              archived: { $ne: true },
              patientId: { $eq: patientId },
              templateId: { $eq: templateId },
            },
          },
        ],
      },
      PrescriptionRequestDB
    );
  }

  /**
   * Filters prescription requests based on whether their single orders have matching integrated care IDs
   * Note: If this gets too inperformant in the future we need to extend the prescriptionRequest DataModel
   * and write the integratedCare ID there as well
   */
  private async filterRequestsByIntegratedCareIds(
    requests: IPrescriptionRequest[],
    integratedCareIds: string[]
  ): Promise<IPrescriptionRequest[]> {
    const filteredRequests: IPrescriptionRequest[] = [];

    for (const request of requests) {
      const singleOrder = await this._queryService.get<ISingleOrder>(request.singleOrderId, SingleOrderDB);
      if (integratedCareIds.includes(singleOrder?.integratedCareId)) {
        filteredRequests.push(request);
      }
    }

    return filteredRequests;
  }

  /**
   * Returns the most recent prescription request from the provided list
   */
  private getMostRecentRequest(requests: IPrescriptionRequest[]): IPrescriptionRequest | undefined {
    if (!requests || requests.length === 0) {
      return undefined;
    }

    return requests.sort(
      (a, b) => new Date((b as any).createdAt).getTime() - new Date((a as any).createdAt).getTime()
    )[0];
  }

  public async getAdditionalPropertiesFromAudit(prescriptionAudit: IAudit): Promise<any> {
    const documentGenerator = new DocumentGenerator(
      new AuditAssetLoader(this._attachmentModel, this._attachmentService),
      new AuditReportGenerator() as unknown as ReportGeneratable
    );
    const auditWithoutPrescriptionElements = this.filterOutPrescriptionElements(prescriptionAudit);
    const auditKeyValuePairs = await documentGenerator.generate<Record<string, string>>(
      auditWithoutPrescriptionElements
    );
    return auditKeyValuePairs?.additionalProperties;
  }

  public async generatePrescriptionRequests(
    prescriptionAuditNodes: IAuditNode[],
    additionalProperties: any
  ): Promise<PrescriptionRequestViewModel[]> {
    const prescriptionNodes = assignIn(new TreeArray<IAuditNode>(), prescriptionAuditNodes).filterRec(
      node => node.nodeType_Id === 'prescriptionData'
    );

    const prescriptions: PrescriptionRequestViewModel[] = [];
    for (const prescriptionNode of prescriptionNodes) {
      const prescriptionDto = await this.generatePrescriptionRequest(prescriptionNode, additionalProperties);
      prescriptions.push(prescriptionDto);
    }
    for (const prescription of prescriptions) {
      await this._prescriptionRequestComponentModel.create(map(prescription, new PrescriptionRequestDto()));
    }

    return prescriptions;
  }

  public async generatePrescriptionRequest(
    prescriptionAuditNode: IAuditNode,
    additionalProperties: any
  ): Promise<PrescriptionRequestViewModel> {
    const prescriptionRequestViewModel: PrescriptionRequestViewModel = observe(new PrescriptionRequestViewModel());
    await this._prescriptionChain.handleRequest(
      prescriptionRequestViewModel,
      prescriptionAuditNode.responses[0].value as unknown as PrescriptionNodeValue
    );
    prescriptionRequestViewModel.additionalProperties = additionalProperties as AuditData;
    prescriptionRequestViewModel.attachments = (
      prescriptionAuditNode.responses[0].value as unknown as PrescriptionNodeValue
    )?.attachments;
    return prescriptionRequestViewModel;
  }

  public async preview(audit: IAudit, node: any): Promise<void> {
    const loading = await this._loadingController.create({
      message: this._translateService.instant('components.swodoc.loadingPreview'),
    });
    await loading.present();
    const closeModal = async () => {
      await loading.dismiss().catch(error => console.log(error));
    };
    const additionalProperties = await this.getAdditionalPropertiesFromAudit(audit);
    const prescriptionRequest = await this.generatePrescriptionRequest(node, additionalProperties);
    const reportEntity = await this._queryService.get<IReport>(audit.templateId, ReportDB);

    const report = new Stimulsoft.Report.StiReport();
    report.load(reportEntity.report);
    const dataSet = new Stimulsoft.System.Data.DataSet('swodoc');
    dataSet.readJson(prescriptionRequest);
    report.regData(dataSet.dataSetName, '', dataSet);
    report.dictionary.synchronize();
    await closeModal();
    const modal = await this._modalController.create({
      component: ReportModal,
      componentProps: { report, name: 'Rezeptanforderung', showSaveButton: true },
      cssClass: 'fullPageModal',
    });
    await modal.present();
  }

  /**
   * Print the prescription request in the browser
   */
  public async openBrowserPrintDialogue(audit: IAudit, PrescriptionNode: any): Promise<void> {
    Stimulsoft.Base.StiLicense.key = environment.stimulsoftLicense;
    const loading = await this._loadingController.create({
      message: this._translateService.instant('prescription.create.preparingPrint'),
    });
    await loading.present();
    const closeModal = async () => {
      await loading.dismiss().catch(error => console.log(error));
    };
    const additionalProperties = await this.getAdditionalPropertiesFromAudit(audit);
    const prescriptionRequest = await this.generatePrescriptionRequest(PrescriptionNode, additionalProperties);
    const reportEntity = await this._queryService.get<IReport>(audit.templateId, ReportDB);
    const report = new Stimulsoft.Report.StiReport();
    report.load(reportEntity.report);
    const dataSet = new Stimulsoft.System.Data.DataSet('swodoc');
    dataSet.readJson(prescriptionRequest);
    report.regData(dataSet.dataSetName, '', dataSet);
    report.dictionary.synchronize();
    // This is fire and forget. We dont know when the popup will be opened
    report.print(null, Stimulsoft.Report.Export.StiHtmlExportMode.Div);
    await closeModal();
  }

  public async changePrescriptionStatus(
    orderViewModel: SingleOrderViewModel,
    componentModel: SingleOrderComponentModel
  ): Promise<void> {
    const statusModal = await this._modalController.create({
      component: SingleOrderPrescriptionStatusComponent,
      cssClass: 'fullPageModal',
      componentProps: {
        prescriptionStatus: orderViewModel.prescriptionStatus,
        patientId: orderViewModel.patientId,
        singleOrderId: orderViewModel._id,
      },
    });
    await statusModal.present();
    const response = await statusModal.onDidDismiss();
    if (response.data) {
      const singleOrderViewModel = observe(map(orderViewModel, new SingleOrderViewModel()));
      singleOrderViewModel.prescriptionStatus = response.data;
      await componentModel.edit(singleOrderViewModel, map(singleOrderViewModel, new SingleOrderDto()), {
        ignoreUpdatedAt: true,
        noNotification: true,
      });
    }
  }

  private filterOutPrescriptionElements(prescriptionAudit: IAudit): IAudit {
    const audit = cloneDeep(prescriptionAudit);
    audit.auditNodes.forEach(section => {
      section.children.map(child => {
        if ((child.nodeType_Id as any) === 'prescriptionData') {
          if (section.children.length > 1) {
            section.children = section.children.filter(item => (item.nodeType_Id as any) !== 'prescriptionData');
            return;
          } else {
            audit.auditNodes = audit.auditNodes.filter(auditNode => auditNode !== section);
            return;
          }
        }
      });
    });
    return audit;
  }
}
